import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  Row,
  FormGroup,
  InputGroupText,
  InputGroup,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Breadcrumbs, P, H5 } from "../../../../../AbstractElements";
import Cleave from "cleave.js/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import ReactQuill from "react-quill-new";
import { Save } from "react-feather";
import Swal from "sweetalert2";

//SERVICES
import {
  APPL_ACTIVITY_LIST_QUERY_KEY,
  applActivityListService,
} from "../../../../../Services/applicantsActivityListServices";
import {
  USER_VIEW_QUERY_KEY,
  ViewuserService,
} from "../../../../../Services/userService";
import {
  APPLICANT_ACTIVITY_QUERY_KEY,
  applicantActivityService,
} from "../../../../../Services/applicantsActivityServices";

//TYPES
import { ApplicantActivityList } from "../../../../../Types/Applicants/ApplicantsActivity";
import { ViewUser } from "../../../../../Types/User/ViewUserModel";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  ApplNo: string;
}

const CreateNoteForm: React.FC<UpdateFormProps> = ({ toggle, ApplNo }) => {
  const currentDate = new Date().toISOString().split("T")[0];
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const { data: activityData, isLoading: isLoading } = useQuery<{
    data: ApplicantActivityList[];
  }>({
    queryKey: APPL_ACTIVITY_LIST_QUERY_KEY,
    queryFn: applActivityListService.getActivityList,
  });

  const activities: ApplicantActivityList[] = [...(activityData?.data ?? [])]
    .filter((item) => item.status === 1)
    .sort((a, b) => a.id - b.id);

  const { data: userData, isLoading: UsersisLoading } = useQuery<ViewUser[]>({
    queryKey: USER_VIEW_QUERY_KEY,
    queryFn: ViewuserService.getAllUserView,
  });

  const Users: ViewUser[] = [...(userData ?? [])].sort((a, b) =>
    a.emp_name.localeCompare(b.emp_name),
  );

  const [formData, setFormData] = useState({
    application_no: ApplNo,
    activity_id: 0,
    action_date: "",
    action_by: 0,
    note: "",
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      applicantActivityService.createActivity(formData),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      if (toggle) toggle();
      Swal.close();
      if (result?.emp_no) {
        Swal.fire({
          allowOutsideClick: false,
          allowEscapeKey: false,
          icon: "success",
          title: "Employee No. " + result.emp_no,
          text: result?.message,
          confirmButtonColor: "#4e73df",
        });
      } else {
        Swal.fire({
          allowOutsideClick: false,
          allowEscapeKey: false,
          icon: "success",
          title: "Transaction Successful",
          text: result?.message,
          confirmButtonColor: "#4e73df",
        });
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Error",
        text: responseData?.message || "Transaction Failed.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      application_no: ApplNo,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const requiresConfirmation = [8, 9, 10, 11].includes(
      Number(formData.activity_id),
    );

    const confirmationTitle =
      Number(formData.activity_id) === 8
        ? "Hire This Applicant?"
        : Number(formData.activity_id) === 9
          ? "Reject This Applicant?"
          : Number(formData.activity_id) === 10
            ? "Cancel This Application?"
            : Number(formData.activity_id) === 11
              ? "Void This Application?"
              : "Save This Activity?";

    const confirmationButton =
      Number(formData.activity_id) === 8
        ? "Hire Applicant"
        : Number(formData.activity_id) === 9
          ? "Reject Applicant"
          : Number(formData.activity_id) === 10
            ? "Cancel Application"
            : Number(formData.activity_id) === 11
              ? "Void Application"
              : "Save Activity";

    Swal.fire({
      title: confirmationTitle,
      text: requiresConfirmation
        ? 'You are about to perform a CRITICAL action. This cannot be undone. Please type "Confirm" to proceed.'
        : "Do you want to save this activity?",
      icon: requiresConfirmation ? "warning" : "question",

      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> ' + confirmationButton,
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,

      ...(requiresConfirmation && {
        input: "text",
        inputPlaceholder: 'Type "Confirm" to continue',
        inputValidator: (value) => {
          if (value.trim() !== "Confirm") {
            return 'Please type "Confirm" to proceed.';
          }
          return null;
        },
      }),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Saving...",
          text: "Please wait while your activity is being saved.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => Swal.showLoading(),
        });

        createMutation.mutate(formData);
      }
    });
  };

  return (
    <Form className="needs-validation" onSubmit={handleSubmit}>
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="p-1">
          <Col md="12">
            <FormGroup>
              <Label>
                Activity <b className="text-danger">*</b>
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-chart-flow-alt-2 txt-primary"></i>
                </InputGroupText>

                <Input
                  className="select-scroll"
                  id="activity_id"
                  name="activity_id"
                  type="select"
                  onChange={(e) => {
                    handleInputChange(e);
                  }}
                  disabled={isLoading}
                  required
                >
                  <option value="" disabled selected>
                    {isLoading
                      ? "Loading activities..."
                      : "- Select Activity -"}
                  </option>

                  {activities.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.activity_name}
                    </option>
                  ))}
                </Input>
              </InputGroup>
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup>
              <Label>
                Action Date <b className="text-danger">*</b>
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-ui-calendar txt-primary"></i>
                </InputGroupText>
                <Input
                  className="digits"
                  type="date"
                  id="action_date"
                  name="action_date"
                  value={formData.action_date}
                  required
                  onChange={handleInputChange}
                />
              </InputGroup>
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup>
              <Label>
                Action By <b className="text-danger">*</b>
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-user-suited txt-primary"></i>
                </InputGroupText>

                <Input
                  className="select-scroll"
                  id="action_by"
                  name="action_by"
                  type="select"
                  onChange={(e) => {
                    handleInputChange(e);
                  }}
                  disabled={UsersisLoading}
                  required
                >
                  <option value="" disabled selected>
                    {UsersisLoading
                      ? "Loading HRs..."
                      : "- Select HR Personnel -"}
                  </option>

                  {Users.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.emp_name}
                    </option>
                  ))}
                </Input>
              </InputGroup>
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup>
              <Label>Additional Note</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-file-alt txt-primary"></i>
                </InputGroupText>

                <Input
                  className="select-scroll"
                  id="note"
                  name="note"
                  type="text"
                  value={formData.note}
                  maxLength={50}
                  onChange={(e) => {
                    handleInputChange(e);
                  }}
                />
              </InputGroup>
            </FormGroup>
          </Col>

          <Col md="12 text-end">
            <Btn
              color="primary"
              type="submit"
              disabled={createMutation.isPending}
            >
              <Save size={16} className="me-1" />
              {createMutation.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Creating Notes...
                </>
              ) : (
                "Save"
              )}
            </Btn>
          </Col>
        </Row>
      </ModalBody>
    </Form>
  );
};

export default CreateNoteForm;
